import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createStoryEditorial, fetchWriters } from "../utils/api";
import {
  Box,
  Button,
  TextField,
  Typography,
  CardMedia,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlaceholderImage from "@mui/icons-material/Image";

export const cropAndResizeImage = (file, targetWidth = 354, targetHeight = 500) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;

      img.onload = function () {
        let width = img.width;
        let height = img.height;

        const inputAspectRatio = width / height;
        const targetAspectRatio = targetWidth / targetHeight;

        let cropX = 0,
          cropY = 0,
          cropWidth = width,
          cropHeight = height;

        if (inputAspectRatio > targetAspectRatio) {
          cropWidth = Math.round(height * targetAspectRatio);
          cropX = Math.round((width - cropWidth) / 2);
        } else if (inputAspectRatio < targetAspectRatio) {
          cropHeight = Math.round(width / targetAspectRatio);
          cropY = Math.round((height - cropHeight) / 2);
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            } else {
              reject(new Error("Image cropping failed"));
            }
          },
          file.type,
          0.8
        );
      };

      img.onerror = () => reject(new Error("Invalid image file"));
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const StoryFormEditorial = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [authorsLoading, setAuthorsLoading] = useState(true);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const categories = ["Romantik", "Fantastik", "Macera", "Dram", "Gençlik", "Bilim Kurgu", "Smut"];

  // Fetch authors list
  useEffect(() => {
    const loadAuthors = async () => {
      try {
        const writerList = await fetchWriters();
        setAuthors(writerList);
      } catch (error) {
        console.error("Error fetching authors:", error);
      } finally {
        setAuthorsLoading(false);
      }
    };
    loadAuthors();
  }, []);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      // Ensure it's an image
      if (!file.type.startsWith("image/")) {
        console.warn("Dropped file is not an image.");
        return;
      }

      handleCoverChange({ target: { files: [file] } });
      e.dataTransfer.clearData();
    } else {
      console.warn("No files detected in drop event.");
    }
  };

  const handleCategoryChange = (event, newCategories) => {
    if (newCategories.length > 2) {
      newCategories = newCategories.slice(-2);
    }
    setSelectedCategories(newCategories);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation (only checking for empty fields and category/author selection)
    if (!title.trim() || !description.trim()) {
      alert("Title and description cannot be empty.");
      return;
    }

    if (selectedCategories.length === 0) {
      alert("Please select at least one category.");
      return;
    }

    if (!selectedAuthor) {
      alert("Please select an author.");
      return;
    }

    setLoading(true);
    try {
      const storyID = await createStoryEditorial({
        title: title.trim(),
        description: description.trim(),
        cover,
        categories: selectedCategories,
        authorId: selectedAuthor.id,
        authorName: selectedAuthor.name,
      });
      navigate(`/editorial/stories/${storyID}`);
    } catch (error) {
      console.error("Error creating story:", error);
      alert(error.message || "Failed to create story.");
    } finally {
      setLoading(false);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      cropAndResizeImage(file, 354, 500)
        .then((resizedFile) => {
          setCover(resizedFile);
          setCoverPreview(URL.createObjectURL(resizedFile));
        })
        .catch((err) => {
          console.error("Image processing failed:", err);
        });
    }
  };

  // Clean up URL object on unmount or cover change
  useEffect(() => {
    return () => {
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 800,
        margin: "auto",
        padding: theme.spacing(3),
      }}
    >
      <Typography variant="h4" gutterBottom textAlign="center">
        Create a New Story (Editorial)
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.spacing(2),
        }}
      >
        {/* Cover Image Upload */}
        <Box
          sx={{
            height: 200,
            width: 140,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: theme.palette.grey[200],
            borderRadius: "4px",
            boxShadow: theme.shadows[2],
            overflow: "hidden",
            cursor: "pointer",
            border: `2px dashed ${isDragging ? "gold" : theme.palette.grey[400]}`,
            transition: "border-color 0.2s ease-in-out",
          }}
          onClick={() => document.getElementById("cover-upload-input").click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {coverPreview ? (
            <CardMedia
              component="img"
              image={coverPreview}
              alt="Cover Preview"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.grey[500],
              }}
            >
              <PlaceholderImage sx={{ fontSize: 64 }} />
              <Typography variant="body2" align="center" color="textSecondary">
                Drag and Drop or Click to Upload
              </Typography>
            </Box>
          )}
        </Box>
        <input
          id="cover-upload-input"
          type="file"
          accept="image/*"
          onChange={handleCoverChange}
          hidden
        />

        {/* Story Details */}
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            options={authors}
            getOptionLabel={(option) => option.name}
            value={selectedAuthor}
            onChange={(event, newValue) => setSelectedAuthor(newValue)}
            loading={authorsLoading}
            sx={{
              mb: 2,
              "& .MuiAutocomplete-popupIndicator": {
                color: theme.palette.text.secondary,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Author"
                required
                sx={{
                  mb: 2,
                  "& label": { color: theme.palette.text.primary.main },
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.primary.main,
                    "& fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.dark,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {authorsLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />


          <TextField
            label="Story Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            sx={{ marginBottom: theme.spacing(2) }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            fullWidth
            multiline
            rows={4}
            sx={{ marginBottom: theme.spacing(2) }}
          />

          <Typography variant="body1" sx={{ mt: 2, marginBottom: theme.spacing(2) }}>
            Select Categories (1-2):
          </Typography>
          <ToggleButtonGroup
            value={selectedCategories}
            onChange={handleCategoryChange}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: theme.spacing(1),
              justifyContent: "center",
            }}
          >
            {categories.map((category) => (
              <ToggleButton
                key={category}
                value={category}
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  minWidth: "80px",
                  height: "40px",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: selectedCategories.includes(category)
                    ? theme.palette.callToAction?.main || theme.palette.primary.main
                    : theme.palette.background.paper,
                  color: selectedCategories.includes(category)
                    ? theme.palette.callToAction?.contrastText || theme.palette.primary.contrastText
                    : theme.palette.text.primary,
                  border: `2px solid ${theme.palette.callToAction?.main || theme.palette.primary.main}`,
                  borderRadius: "8px !important",
                  "&:hover": {
                    backgroundColor: selectedCategories.includes(category)
                      ? theme.palette.callToAction?.dark || theme.palette.primary.dark
                      : theme.palette.action.hover,
                    borderColor: theme.palette.callToAction?.dark || theme.palette.primary.dark,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.callToAction?.main || theme.palette.primary.main,
                    color: theme.palette.callToAction?.contrastText || theme.palette.primary.contrastText,
                    border: `2px solid ${theme.palette.callToAction?.dark || theme.palette.primary.dark} !important`,
                    "&:hover": {
                      backgroundColor: theme.palette.callToAction?.dark || theme.palette.primary.dark,
                    },
                  },
                  "&:not(:first-of-type)": {
                    borderLeft: `2px solid ${theme.palette.callToAction.main} !important`,
                    marginLeft: "-2px !important",
                  },
                  "&:first-of-type": {
                    borderTopLeftRadius: "8px !important",
                    borderBottomLeftRadius: "8px !important",
                  },
                  "&:last-of-type": {
                    borderTopRightRadius: "8px !important",
                    borderBottomRightRadius: "8px !important",
                  },
                }}
              >
                {category}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || !selectedAuthor || selectedCategories.length === 0}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
        sx={{ marginTop: theme.spacing(3) }}
      >
        {loading ? "Submitting..." : "Confirm"}
      </Button>
    </Box>
  );
};

export default StoryFormEditorial;
